/* eslint-disable @next/next/no-img-element */
"use client";

import { LinkCard } from "@/components/common/link-card";
import { cn, formatImageUrl } from "@/lib/utils";
import { TEditorType, useCustomEditor } from "@/providers/custom-editor-provider";
import { TPostLink, TPostMedia } from "@/types/post";
import { GalleryHorizontalIcon, LayoutPanelTopIcon, LinkIcon, Trash2Icon } from "lucide-react";

export const EditorMediaAndLinksPreviewUI = ({
	images,
	editorType,
	linkCards,
	imageViewType,
	...props
}: {
	// TODO (zeekhuge): Since TPostMedia#id is optional now, we don't need this Omit below.
	// Also, this type has been used at a lot of places
	images: Array<Omit<TPostMedia, "id">>;
	editorType: TEditorType;
	imageViewType: "grid" | "carousel";
	linkCards: TPostLink[];
	onRemoveImage?: (index: number) => void;
	onRemoveLinkCard?: (ulr: string) => void;
	onImageViewTypeChange?: (viewType: "grid" | "carousel") => void;
}) => {
	if (editorType === "comment") {
		return (
			<div className="rounded-lg bg-brand-primary/5 p-3">
				<div className="grid grid-cols-2 gap-2 md:grid-cols-4">
					{images.map((img, idx) => (
						<div key={idx} className="relative">
							<img
								src={formatImageUrl(img.objectPath)}
								alt={img.alt || `Image ${idx + 1}`}
								className="h-24 w-full rounded object-cover"
							/>

							<button
								onClick={() => props.onRemoveImage?.(idx)}
								className="absolute right-1 top-1 rounded-full bg-gray-100 p-1 text-gray-700"
							>
								<Trash2Icon className="h-4 w-4" />
							</button>
						</div>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="relative mb-4 rounded-lg bg-brand-primary/5 p-3">
			<div className="absolute bottom-4 right-3 z-20 flex gap-2">
				<button
					onClick={() => props.onImageViewTypeChange?.("carousel")}
					className={cn(
						"rounded-md p-1",
						imageViewType === "carousel" ? "bg-brand-primary text-white" : ""
					)}
				>
					<GalleryHorizontalIcon className="h-4 w-4" />
				</button>

				{!linkCards.length && images.length > 2 && (
					<button
						onClick={() => props.onImageViewTypeChange?.("grid")}
						className={cn(
							"rounded-md p-1",
							imageViewType === "grid" ? "bg-brand-primary text-white" : ""
						)}
					>
						<LayoutPanelTopIcon className="h-4 w-4" />
					</button>
				)}
			</div>

			<div className="grid grid-cols-2 gap-2 md:grid-cols-4">
				{images.map((img, idx) => (
					<div key={idx} className="relative">
						<img
							src={formatImageUrl(img.objectPath)}
							alt={img.alt || `Image ${idx + 1}`}
							className="h-24 w-full rounded object-cover"
						/>

						<button
							onClick={() => props.onRemoveImage?.(idx)}
							className="absolute right-1 top-1 rounded-full bg-gray-100 p-1 text-gray-700"
						>
							<Trash2Icon className="h-4 w-4" />
						</button>
					</div>
				))}

				{linkCards.map((linkCard, idx) => (
					<div key={idx} className="relative">
						<button
							onClick={() => props.onRemoveLinkCard?.(linkCard.url)}
							className="absolute left-1 top-1 rounded-full bg-white p-1 text-gray-700 opacity-80"
						>
							<LinkIcon className="h-4 w-4" />
						</button>

						<LinkCard link={linkCard} size="small" />

						<button
							onClick={() => props.onRemoveLinkCard?.(linkCard.url)}
							className="absolute right-1 top-1 z-20 rounded-full bg-gray-100 p-1 text-gray-700 transition-all duration-300 hover:bg-red-500 hover:text-white"
						>
							<Trash2Icon className="h-4 w-4" />
						</button>
					</div>
				))}
			</div>
		</div>
	);
};

const EditorMediaAndLinksPreview = () => {
	const {
		imageViewType,
		images,
		setImages,
		setImageViewType,
		linkCards,
		removeLinkCard,
		editorType,
	} = useCustomEditor();

	if (editorType === "article") return null;

	if (!images.length && !linkCards.length) return null;

	return (
		<EditorMediaAndLinksPreviewUI
			images={images}
			editorType={editorType}
			imageViewType={imageViewType}
			linkCards={linkCards}
			onImageViewTypeChange={setImageViewType}
			onRemoveImage={(i) => setImages(images.filter((_, j) => j !== i))}
			onRemoveLinkCard={(i) => removeLinkCard(i)}
		/>
	);
};

export default EditorMediaAndLinksPreview;
